/**
 * Marketplace SKU Import Modal
 * Import SKU mappings from Excel/CSV or add manually
 * Links marketplace SKUs to products via product_sku
 */

import React, { useState, useRef } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Loader2, Link2, Plus, Trash2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { getAllProducts } from '../../utils/database';
import type { Product } from '../../types/database';
import type { FulfillmentOption } from '../../types/database';
import { logger } from '../../utils/logger';
import { useModalA11y } from '../../hooks/useModalA11y';

// FIX 12: Use relative path instead of localhost
const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface MarketplaceSKUImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  marketplaceName: string;
  marketplaceShortName: string;
  marketplaceId: string;
  fulfillmentOptions: FulfillmentOption[];
  selectedFulfillment?: string;
  isAmazon?: boolean; // Show ASIN column for Amazon
}

interface ParsedSKUData {
  marketplaceSku: string;
  productSku: string;
  countryCode: string;
  asin: string;
  listingPrice: number | null;
  fulfillmentType: string;
}

interface MatchedProduct {
  parsed: ParsedSKUData;
  product: Product | null;
  matchType: 'exact' | 'partial' | 'none';
}

interface ManualEntry {
  id: string;
  marketplaceSku: string;
  productSku: string;
  countryCode: string;
  asin: string;
  listingPrice: string;
  fulfillmentType: string;
  product: Product | null;
}

interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
}

const MarketplaceSKUImportModal: React.FC<MarketplaceSKUImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  marketplaceName,
  marketplaceShortName,
  marketplaceId,
  fulfillmentOptions,
  selectedFulfillment: initialFulfillment,
  isAmazon = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [matchedData, setMatchedData] = useState<MatchedProduct[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [mode, setMode] = useState<'choose' | 'import' | 'manual' | 'preview' | 'result'>('choose');

  const defaultFulfillment = initialFulfillment || fulfillmentOptions[0]?.code || 'FBA';

  // Manual entry state
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([
    { id: '1', marketplaceSku: '', productSku: '', countryCode: 'US', asin: '', listingPrice: '', fulfillmentType: defaultFulfillment, product: null }
  ]);
  const [products, setProducts] = useState<Product[]>([]);
  const [searchResults, setSearchResults] = useState<Record<string, Product[]>>({});

  // ref captures latest handleClose for Escape handler (handleClose is defined below)
  const handleCloseRef = useRef<() => void>(() => onClose());
  const modalRef = useModalA11y(isOpen, () => handleCloseRef.current());

  if (!isOpen) return null;

  // Load products for manual entry
  const loadProducts = async () => {
    if (products.length === 0) {
      const allProducts = await getAllProducts();
      setProducts(allProducts);
    }
  };

  const parseExcelFile = async (file: File): Promise<ParsedSKUData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as Record<string, string | number | null>[];

          if (jsonData.length === 0) {
            reject(new Error('Dosya boş veya okunamadı'));
            return;
          }

          const firstRow = jsonData[0] as Record<string, string | number | null>;
          const columns = Object.keys(firstRow);

          const findColumn = (names: string[]): string | null => {
            for (const name of names) {
              const found = columns.find(c => c.toLowerCase().trim() === name.toLowerCase());
              if (found) return found;
            }
            return null;
          };

          // Marketplace SKU - zorunlu
          const skuCol = findColumn([
            'marketplace_sku', 'seller_sku', 'seller-sku', 'sku',
            'marketplace-sku', 'item_sku', 'listing_sku',
          ]);

          // Product SKU - zorunlu (our product code - AHM00102S4FZ formatında)
          const productSkuCol = findColumn([
            'product_sku', 'productsku', 'our_sku', 'iwasku',
            'internal_sku', 'product_code', 'urun_kodu',
          ]);

          // Country code - zorunlu
          const countryCol = findColumn([
            'country', 'country_code', 'marketplace', 'ulke',
            'market', 'region',
          ]);

          // ASIN - Amazon için zorunlu
          const asinCol = findColumn([
            'asin', 'asin1', 'product_asin', 'fnsku',
          ]);

          // Price - opsiyonel
          const priceCol = findColumn([
            'price', 'listing_price', 'your_price', 'fiyat',
            'item_price', 'satış_fiyatı',
          ]);

          // Fulfillment type - zorunlu
          const fulfillmentCol = findColumn([
            'fulfillment', 'fulfillment_type', 'fulfillment_channel',
            'fba_fbm', 'shipping_type', 'karsilama',
          ]);

          // Validate required columns
          if (!skuCol) {
            reject(new Error('marketplace_sku kolonu bulunamadı. Desteklenen: marketplace_sku, seller_sku, sku'));
            return;
          }
          if (!productSkuCol) {
            reject(new Error('product_sku kolonu bulunamadı. Desteklenen: product_sku, our_sku, iwasku, product_code'));
            return;
          }
          if (!countryCol) {
            reject(new Error('country kolonu bulunamadı. Desteklenen: country, country_code, marketplace'));
            return;
          }
          if (isAmazon && !asinCol) {
            reject(new Error('Amazon için asin kolonu zorunludur. Desteklenen: asin, asin1'));
            return;
          }
          if (!fulfillmentCol) {
            reject(new Error('fulfillment kolonu bulunamadı. Desteklenen: fulfillment, fulfillment_type, fba_fbm'));
            return;
          }

          const skuData: ParsedSKUData[] = jsonData
            .map((row: Record<string, string | number | null>) => {
              const marketplaceSku = String(row[skuCol] || '').trim();
              const countryCode = String(row[countryCol] || '').trim().toUpperCase();
              const asin = asinCol ? String(row[asinCol] || '').trim() : '';
              const fulfillmentType = String(row[fulfillmentCol!] || '').trim().toUpperCase();

              // Product SKU: direkt kolonu kullan (AHM00102S4FZ formatında)
              const productSku = productSkuCol ? String(row[productSkuCol] || '').trim() : '';

              let listingPrice: number | null = null;
              if (priceCol && row[priceCol] !== undefined && row[priceCol] !== '') {
                const priceValue = String(row[priceCol]).replace(/[^0-9.,]/g, '').replace(',', '.');
                const parsed = parseFloat(priceValue);
                if (!isNaN(parsed)) listingPrice = parsed;
              }

              return { marketplaceSku, productSku, countryCode, asin, listingPrice, fulfillmentType };
            })
            .filter((item) => item.marketplaceSku && item.productSku && item.countryCode && item.fulfillmentType);

          resolve(skuData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  };

  const matchProductsByProductSku = async (skuData: ParsedSKUData[]): Promise<MatchedProduct[]> => {
    const allProducts = await getAllProducts();

    // Create lookup by productSku field (exact match)
    const productSkuMap = new Map<string, Product>();
    allProducts.forEach(p => {
      if (p.productSku) {
        productSkuMap.set(p.productSku.toUpperCase(), p);
      }
    });

    return skuData.map(parsed => {
      // Direct product_sku match from product.productSku field
      const exactMatch = productSkuMap.get(parsed.productSku.toUpperCase());
      if (exactMatch) {
        return { parsed, product: exactMatch, matchType: 'exact' as const };
      }

      return { parsed, product: null, matchType: 'none' as const };
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setResult(null);

    try {
      const parsed = await parseExcelFile(file);
      const matched = await matchProductsByProductSku(parsed);
      setMatchedData(matched);
      setMode('preview');
    } catch (error) {
      logger.error('Parse error:', error);
      alert(error instanceof Error ? error.message : 'Dosya okunamadı');
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    setIsLoading(true);

    const importResult: ImportResult = {
      success: true,
      created: 0,
      updated: 0,
      errors: [],
    };

    try {
      const mappings = matchedData
        .filter(m => m.product && m.matchType !== 'none')
        .map(m => ({
          product_id: m.product!.id,
          marketplace_id: marketplaceId,
          country_code: m.parsed.countryCode,
          sku: m.parsed.marketplaceSku,
          asin: m.parsed.asin || null,
          listing_price: m.parsed.listingPrice,
          fulfillment_type: m.parsed.fulfillmentType,
          status: 'active',
        }));

      if (mappings.length === 0) {
        importResult.success = false;
        importResult.errors.push('Eşleşen kayıt bulunamadı');
        setResult(importResult);
        setMode('result');
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('sso_access_token');
      const response = await fetch(`${API_BASE}/sku/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mappings }),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const apiResult = await response.json();
      const resultData = apiResult.data || apiResult;
      importResult.created = resultData.created || 0;
      importResult.updated = resultData.updated || 0;
      if (resultData.errors?.length) importResult.errors = resultData.errors;

      setResult(importResult);
      setMode('result');

      if (importResult.created > 0 || importResult.updated > 0) {
        onImportComplete();
      }
    } catch (error) {
      importResult.success = false;
      importResult.errors.push(error instanceof Error ? error.message : 'Import hatası');
      setResult(importResult);
      setMode('result');
    } finally {
      setIsLoading(false);
    }
  };

  // Manual entry handlers
  const handleManualStart = async () => {
    await loadProducts();
    setMode('manual');
  };

  const addManualEntry = () => {
    setManualEntries([
      ...manualEntries,
      { id: Date.now().toString(), marketplaceSku: '', productSku: '', countryCode: 'US', asin: '', listingPrice: '', fulfillmentType: defaultFulfillment, product: null }
    ]);
  };

  const removeManualEntry = (id: string) => {
    if (manualEntries.length > 1) {
      setManualEntries(manualEntries.filter(e => e.id !== id));
    }
  };

  const updateManualEntry = (id: string, field: keyof ManualEntry, value: string) => {
    setManualEntries(manualEntries.map(e => {
      if (e.id === id) {
        const updated = { ...e, [field]: value };

        // If productSku changed, search for matching product
        if (field === 'productSku' && value.length >= 2) {
          const matches = products.filter(p =>
            p.name.toLowerCase().includes(value.toLowerCase())
          ).slice(0, 5);
          setSearchResults({ ...searchResults, [id]: matches });

          // Auto-select if exact match
          const exactMatch = products.find(p => {
            const match = p.name.match(/^([A-Z]{1,4}-\d{2,4})/);
            return match && match[1].toUpperCase() === value.toUpperCase();
          });
          if (exactMatch) {
            updated.product = exactMatch;
          }
        }

        return updated;
      }
      return e;
    }));
  };

  const selectProduct = (entryId: string, product: Product) => {
    setManualEntries(manualEntries.map(e =>
      e.id === entryId ? { ...e, product } : e
    ));
    setSearchResults({ ...searchResults, [entryId]: [] });
  };

  const handleManualImport = async () => {
    setIsLoading(true);

    const importResult: ImportResult = {
      success: true,
      created: 0,
      updated: 0,
      errors: [],
    };

    try {
      const validEntries = manualEntries.filter(e =>
        e.marketplaceSku && e.countryCode && e.product
      );

      if (validEntries.length === 0) {
        importResult.success = false;
        importResult.errors.push('Geçerli kayıt bulunamadı. SKU, ülke ve ürün seçimi zorunludur.');
        setResult(importResult);
        setMode('result');
        setIsLoading(false);
        return;
      }

      const mappings = validEntries.map(e => ({
        product_id: e.product!.id,
        marketplace_id: marketplaceId,
        country_code: e.countryCode,
        sku: e.marketplaceSku,
        asin: e.asin || null,
        listing_price: e.listingPrice ? parseFloat(e.listingPrice) : null,
        fulfillment_type: e.fulfillmentType,
        status: 'active',
      }));

      const token = localStorage.getItem('sso_access_token');
      const response = await fetch(`${API_BASE}/sku/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mappings }),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const apiResult = await response.json();
      const resultData = apiResult.data || apiResult;
      importResult.created = resultData.created || 0;
      importResult.updated = resultData.updated || 0;
      if (resultData.errors?.length) importResult.errors = resultData.errors;

      setResult(importResult);
      setMode('result');

      if (importResult.created > 0 || importResult.updated > 0) {
        onImportComplete();
      }
    } catch (error) {
      importResult.success = false;
      importResult.errors.push(error instanceof Error ? error.message : 'Kayıt hatası');
      setResult(importResult);
      setMode('result');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setMatchedData([]);
    setResult(null);
    setMode('choose');
    setManualEntries([{ id: '1', marketplaceSku: '', productSku: '', countryCode: 'US', asin: '', listingPrice: '', fulfillmentType: defaultFulfillment, product: null }]);
    onClose();
  };
  // keep ref pointing at latest handleClose so Escape triggers cleanup
  handleCloseRef.current = handleClose;

  const matchedCount = matchedData.filter(m => m.matchType !== 'none').length;
  const unmatchedCount = matchedData.filter(m => m.matchType === 'none').length;

  // Export unmatched SKUs to Excel
  const exportUnmatchedSKUs = () => {
    const unmatchedData = matchedData.filter(m => m.matchType === 'none');

    if (unmatchedData.length === 0) {
      alert('Eşleşmeyen SKU yok');
      return;
    }

    const exportData = unmatchedData.map(m => ({
      'Marketplace SKU': m.parsed.marketplaceSku,
      'Product SKU (IWASKU)': m.parsed.productSku,
      'Country': m.parsed.countryCode,
      'Fulfillment': m.parsed.fulfillmentType,
      'ASIN': m.parsed.asin || '',
      'Price': m.parsed.listingPrice || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unmatched SKUs');

    // Auto-size columns
    const colWidths = [
      { wch: 20 }, // Marketplace SKU
      { wch: 20 }, // Product SKU
      { wch: 10 }, // Country
      { wch: 12 }, // Fulfillment
      { wch: 15 }, // ASIN
      { wch: 10 }, // Price
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `${marketplaceShortName}_unmatched_skus_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mp-sku-import-title"
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center gap-3 text-white">
            <Link2 className="w-6 h-6" aria-hidden="true" />
            <div>
              <h2 id="mp-sku-import-title" className="text-xl font-bold">{marketplaceName} SKU Eşleştirme</h2>
              <p className="text-sm opacity-90">Marketplace SKU'larını ürünlerle eşleştir</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white" aria-label="Kapat">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Choose Mode */}
          {mode === 'choose' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Excel Import */}
                <button
                  onClick={() => setMode('import')}
                  className="p-6 border-2 border-dashed rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
                >
                  <span className="text-4xl mb-3 block">📊</span>
                  <h3 className="font-semibold text-slate-800">Excel/CSV Import</h3>
                  <p className="text-sm text-slate-500 mt-1">Toplu SKU eşleştirmesi için dosya yükle</p>
                </button>

                {/* Manual Entry */}
                <button
                  onClick={handleManualStart}
                  className="p-6 border-2 border-dashed rounded-xl hover:border-green-400 hover:bg-green-50 transition-colors text-left"
                >
                  <span className="text-4xl mb-3 block">➕</span>
                  <h3 className="font-semibold text-slate-800">Manuel Giriş</h3>
                  <p className="text-sm text-slate-500 mt-1">Tek tek SKU ekle</p>
                </button>
              </div>
            </div>
          )}

          {/* Import Mode */}
          {mode === 'import' && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Zorunlu Kolonlar</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium text-blue-600">marketplace_sku:</span> <span className="text-blue-800">seller_sku, sku</span></div>
                  <div><span className="font-medium text-blue-600">product_sku:</span> <span className="text-blue-800">our_sku, iwasku</span></div>
                  <div><span className="font-medium text-blue-600">country:</span> <span className="text-blue-800">country_code, marketplace</span></div>
                  <div><span className="font-medium text-blue-600">fulfillment:</span> <span className="text-blue-800">fulfillment_type, fba_fbm</span></div>
                  {isAmazon && <div><span className="font-medium text-blue-600">asin:</span> <span className="text-blue-800">asin1, fnsku</span></div>}
                </div>
                <p className="text-xs text-blue-600 mt-2">Opsiyonel: price, listing_price</p>
              </div>

              <label className="block">
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer">
                  {isLoading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
                      <p className="text-slate-600">Dosya işleniyor...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-12 h-12 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-700">Excel veya CSV dosyası seçin</p>
                        <p className="text-sm text-slate-500">.xlsx, .xls, .csv</p>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isLoading}
                  />
                </div>
              </label>

              <button onClick={() => setMode('choose')} className="text-slate-500 hover:text-slate-700 text-sm">
                ← Geri
              </button>
            </div>
          )}

          {/* Manual Entry Mode */}
          {mode === 'manual' && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 font-medium text-slate-600">Marketplace SKU*</th>
                      <th className="text-left p-2 font-medium text-slate-600">IWASKU*</th>
                      <th className="text-left p-2 font-medium text-slate-600 w-20">Ülke*</th>
                      <th className="text-left p-2 font-medium text-slate-600 w-24">Fulfillment*</th>
                      {isAmazon && <th className="text-left p-2 font-medium text-slate-600">ASIN</th>}
                      <th className="text-left p-2 font-medium text-slate-600 w-24">Fiyat</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {manualEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="p-2">
                          <input
                            type="text"
                            value={entry.marketplaceSku}
                            onChange={(e) => updateManualEntry(entry.id, 'marketplaceSku', e.target.value)}
                            placeholder="SKU-123"
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="p-2 relative">
                          <input
                            type="text"
                            value={entry.productSku}
                            onChange={(e) => updateManualEntry(entry.id, 'productSku', e.target.value)}
                            placeholder="AHM00102S4FZ"
                            className={`w-full px-2 py-1 border rounded text-sm ${entry.product ? 'border-green-500 bg-green-50' : ''}`}
                          />
                          {entry.product && (
                            <div className="text-xs text-green-600 truncate">{entry.product.name}</div>
                          )}
                          {searchResults[entry.id]?.length > 0 && !entry.product && (
                            <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                              {searchResults[entry.id].map(p => (
                                <button
                                  key={p.id}
                                  onClick={() => selectProduct(entry.id, p)}
                                  className="w-full px-2 py-1 text-left text-xs hover:bg-purple-50 truncate"
                                >
                                  {p.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          <select
                            value={entry.countryCode}
                            onChange={(e) => updateManualEntry(entry.id, 'countryCode', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          >
                            <option value="US">US</option>
                            <option value="CA">CA</option>
                            <option value="MX">MX</option>
                            <option value="UK">UK</option>
                            <option value="DE">DE</option>
                            <option value="FR">FR</option>
                            <option value="IT">IT</option>
                            <option value="ES">ES</option>
                            <option value="NL">NL</option>
                            <option value="PL">PL</option>
                            <option value="SE">SE</option>
                            <option value="JP">JP</option>
                            <option value="AU">AU</option>
                            <option value="AE">AE</option>
                            <option value="SA">SA</option>
                            <option value="TR">TR</option>
                          </select>
                        </td>
                        <td className="p-2">
                          <select
                            value={entry.fulfillmentType}
                            onChange={(e) => updateManualEntry(entry.id, 'fulfillmentType', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          >
                            {fulfillmentOptions.map(opt => (
                              <option key={opt.code} value={opt.code}>{opt.name}</option>
                            ))}
                          </select>
                        </td>
                        {isAmazon && (
                          <td className="p-2">
                            <input
                              type="text"
                              value={entry.asin}
                              onChange={(e) => updateManualEntry(entry.id, 'asin', e.target.value)}
                              placeholder="B0XXXXXXXX"
                              className="w-full px-2 py-1 border rounded text-sm"
                            />
                          </td>
                        )}
                        <td className="p-2">
                          <input
                            type="text"
                            value={entry.listingPrice}
                            onChange={(e) => updateManualEntry(entry.id, 'listingPrice', e.target.value)}
                            placeholder="29.99"
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => removeManualEntry(entry.id)}
                            className="p-1 text-slate-400 hover:text-red-500"
                            disabled={manualEntries.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={addManualEntry}
                className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
              >
                <Plus className="w-4 h-4" /> Satır Ekle
              </button>

              <div className="flex justify-between items-center pt-4 border-t">
                <button onClick={() => setMode('choose')} className="text-slate-500 hover:text-slate-700 text-sm">
                  ← Geri
                </button>
                <button
                  onClick={handleManualImport}
                  disabled={isLoading || !manualEntries.some(e => e.marketplaceSku && e.product)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Kaydet
                </button>
              </div>
            </div>
          )}

          {/* Preview Step */}
          {mode === 'preview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-slate-800">{matchedData.length}</div>
                  <div className="text-sm text-slate-500">Toplam</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{matchedCount}</div>
                  <div className="text-sm text-green-600">Eşleşen</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{unmatchedCount}</div>
                  <div className="text-sm text-amber-600">Eşleşmeyen</div>
                  {unmatchedCount > 0 && (
                    <button
                      onClick={exportUnmatchedSKUs}
                      className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 rounded transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      Excel İndir
                    </button>
                  )}
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-slate-600">Durum</th>
                        <th className="text-left p-2 font-medium text-slate-600">Marketplace SKU</th>
                        <th className="text-left p-2 font-medium text-slate-600">IWASKU</th>
                        <th className="text-left p-2 font-medium text-slate-600">Ülke</th>
                        <th className="text-left p-2 font-medium text-slate-600">Fulfillment</th>
                        {isAmazon && <th className="text-left p-2 font-medium text-slate-600">ASIN</th>}
                        <th className="text-right p-2 font-medium text-slate-600">Fiyat</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {matchedData.slice(0, 100).map((match, idx) => (
                        <tr key={idx} className={match.matchType === 'none' ? 'bg-red-50' : ''}>
                          <td className="p-2">
                            {match.matchType !== 'none' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </td>
                          <td className="p-2 font-mono text-xs">{match.parsed.marketplaceSku}</td>
                          <td className="p-2">
                            <span className="font-mono text-xs">{match.parsed.productSku}</span>
                            {match.product && (
                              <div className="text-xs text-slate-500 truncate">{match.product.name}</div>
                            )}
                          </td>
                          <td className="p-2">{match.parsed.countryCode}</td>
                          <td className="p-2 text-xs">{match.parsed.fulfillmentType}</td>
                          {isAmazon && <td className="p-2 font-mono text-xs">{match.parsed.asin || '-'}</td>}
                          <td className="p-2 text-right font-mono text-xs">
                            {match.parsed.listingPrice ? `$${match.parsed.listingPrice.toFixed(2)}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <button onClick={() => { setMode('import'); setMatchedData([]); }} className="text-slate-500 hover:text-slate-700 text-sm">
                  ← Geri
                </button>
                <button
                  onClick={handleImport}
                  disabled={isLoading || matchedCount === 0}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {matchedCount} SKU Kaydet
                </button>
              </div>
            </div>
          )}

          {/* Result Step */}
          {mode === 'result' && result && (
            <div className="space-y-6">
              <div className={`rounded-xl p-6 ${result.created > 0 || result.updated > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-3 mb-4">
                  {result.created > 0 || result.updated > 0 ? (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  ) : (
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  )}
                  <h3 className={`text-xl font-bold ${result.created > 0 || result.updated > 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {result.created > 0 || result.updated > 0 ? 'Tamamlandı!' : 'Başarısız'}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{result.created}</div>
                    <div className="text-sm text-slate-600">Yeni</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{result.updated}</div>
                    <div className="text-sm text-slate-600">Güncellenen</div>
                  </div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-medium text-red-700 mb-2">Hatalar:</h4>
                  <ul className="text-sm text-red-600 space-y-1">
                    {result.errors.map((err, idx) => <li key={idx}>• {err}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <button onClick={handleClose} className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700">
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceSKUImportModal;
